import React, { useEffect, useMemo, useState } from 'react';
import { Plots, errorMessage } from '../../api/client';
import { Plot, PlotFlag, PlotRef } from '../../types';
import { Icon } from '../ui/Icon';
import { Confirm, Empty, Loading, Note, Section } from '../ui/primitives';

interface Props {
  plotRef: PlotRef;
  plot: Plot;
  refreshKey: number;
}

const FLAG_RE = /^[a-z0-9_-]{1,64}$/i;

/** PlotSquared flag names offered as suggestions; the plot server is the authority and rejects anything it doesn't know. */
const KNOWN_FLAGS = [
  'animal-attack', 'animal-cap', 'animal-interact', 'beacon-effects', 'block-burn', 'block-ignition', 'blocked-cmds', 'break',
  'chat', 'concrete-harden', 'copper-oxide', 'coral-dry', 'crop-grow', 'deny-exit', 'deny-portal-travel', 'deny-portals',
  'deny-teleport', 'description', 'device-interact', 'disable-physics', 'drop-protection', 'edit-sign', 'entity-cap',
  'entity-change-block', 'explosion', 'farewell', 'feed', 'fishing', 'fly', 'forcefield', 'gamemode', 'grass-grow', 'greeting',
  'guest-gamemode', 'hanging-break', 'hanging-place', 'heal', 'hide-info', 'hostile-attack', 'hostile-cap', 'hostile-interact',
  'ice-form', 'ice-melt', 'instabreak', 'invincible', 'item-drop', 'keep-inventory', 'kelp-grow', 'leaf-decay',
  'lectern-read-book', 'liquid-flow', 'misc-break', 'misc-cap', 'misc-interact', 'misc-place', 'mob-break', 'mob-cap',
  'mob-place', 'music', 'mycel-grow', 'no-worldedit', 'notify-enter', 'notify-leave', 'place', 'player-interact',
  'prevent-creative-copy', 'projectile-change-block', 'projectiles', 'pve', 'pvp', 'redstone', 'sculk-sensor-interact',
  'snow-form', 'snow-melt', 'soil-dry', 'tamed-attack', 'tamed-interact', 'tile-drop', 'time', 'titles', 'untrusted-visit',
  'use', 'vehicle-break', 'vehicle-cap', 'vehicle-place', 'vehicle-use', 'villager-interact', 'vine-grow', 'weather',
];

/** Flags tab: the flags set on the plot, with inline edit, remove (back to the server default) and "set a flag". */
const PlotFlagsTab: React.FC<Props> = ({ plotRef, plot, refreshKey }) => {
  const [flags, setFlags] = useState<PlotFlag[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newName, setNewName] = useState('');
  const [newValue, setNewValue] = useState('');
  const [setting, setSetting] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [pendingRemove, setPendingRemove] = useState<PlotFlag | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(flags == null);
    Plots.flags(plotRef)
      .then((f) => {
        if (!cancelled) {
          setFlags(f);
          setError(null);
        }
      })
      .catch((e) => !cancelled && setError(errorMessage(e, 'Failed to load the plot flags.')))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plotRef, refreshKey]);

  const sorted = useMemo(() => [...(flags || [])].sort((a, b) => a.name.localeCompare(b.name)), [flags]);
  const newNameOk = FLAG_RE.test(newName.trim());
  const canSet = newNameOk && newValue.trim().length > 0 && !setting;

  const upsert = (flag: PlotFlag) => setFlags((prev) => [...(prev || []).filter((f) => f.name !== flag.name), flag]);

  const setFlag = async () => {
    if (!canSet) return;
    const name = newName.trim().toLowerCase();
    setSetting(true);
    setAddError(null);
    setNotice(null);
    try {
      const saved = await Plots.setFlag(plotRef, name, newValue.trim());
      upsert(saved);
      setNewName('');
      setNewValue('');
      setNotice(`${saved.name} is now ${saved.value}.`);
    } catch (e) {
      setAddError(errorMessage(e, `Could not set ${name}. Please try again.`));
    } finally {
      setSetting(false);
    }
  };

  const startEdit = (flag: PlotFlag) => {
    setEditing(flag.name);
    setDraft(flag.value);
    setEditError(null);
    setNotice(null);
  };

  const saveEdit = async () => {
    if (!editing || saving || !draft.trim()) return;
    setSaving(true);
    setEditError(null);
    try {
      const saved = await Plots.setFlag(plotRef, editing, draft.trim());
      upsert(saved);
      setEditing(null);
      setNotice(`${saved.name} is now ${saved.value}.`);
    } catch (e) {
      setEditError(errorMessage(e, `Could not change ${editing}. Please try again.`));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!pendingRemove) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await Plots.removeFlag(plotRef, pendingRemove.name);
      setFlags((prev) => (prev || []).filter((f) => f.name !== pendingRemove.name));
      setNotice(`${pendingRemove.name} is back to the server default.`);
      setPendingRemove(null);
    } catch (e) {
      setRemoveError(errorMessage(e, 'Could not remove that flag. Please try again.'));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <Section title="Plot flags" blurb="The same flags and values as /plot flag set in-game. A flag you haven't set uses the server default.">
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              className="input input--mono"
              list="plot-flag-names"
              placeholder="flag, e.g. pvp"
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value);
                setAddError(null);
                setNotice(null);
              }}
              style={{ width: 200 }}
              maxLength={64}
              disabled={setting}
              aria-label="Flag name"
            />
            <datalist id="plot-flag-names">
              {KNOWN_FLAGS.map((f) => (
                <option key={f} value={f} />
              ))}
            </datalist>
            <input
              className="input input--mono"
              placeholder="value, e.g. true"
              value={newValue}
              onChange={(e) => {
                setNewValue(e.target.value);
                setAddError(null);
                setNotice(null);
              }}
              onKeyDown={(e) => e.key === 'Enter' && setFlag()}
              style={{ width: 240 }}
              disabled={setting}
              aria-label="Flag value"
            />
            <button type="button" className="btn btn--primary" onClick={setFlag} disabled={!canSet}>
              {setting ? <span className="spin" style={{ borderTopColor: '#fff' }} /> : <Icon name="plus" size={12} />} Set flag
            </button>
            {newName && !newNameOk && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Flag names are letters, numbers and dashes, like mob-place.</span>}
          </div>
          <small style={{ display: 'block', marginTop: 8, fontSize: 11.5, color: 'var(--ink3)' }}>
            Values are written as in-game: <span className="kbd">pvp true</span>, <span className="kbd">weather clear</span>, <span className="kbd">time 6000</span>,{' '}
            <span className="kbd">greeting Welcome!</span>. Changes apply straight away.
          </small>
          {addError && (
            <div style={{ marginTop: 12 }}>
              <Note tone="danger">{addError}</Note>
            </div>
          )}
          {notice && !addError && (
            <div style={{ marginTop: 12 }}>
              <Note tone="ok">{notice}</Note>
            </div>
          )}
        </div>

        {error && <Note tone="danger">{error}</Note>}
        {loading && !flags ? (
          <Loading />
        ) : sorted.length === 0 ? (
          !error && (
            <Empty icon="settings" title="No flags set">
              Plot {plot.plotId} uses the server defaults for everything. Set a flag above to change how it behaves.
            </Empty>
          )
        ) : (
          <div className="env">
            {sorted.map((f) => (
              <div className="env-row" key={f.name}>
                <span>
                  <b>{f.name}</b>
                  {editing === f.name && editError && <small style={{ color: 'var(--danger)' }}>{editError}</small>}
                </span>
                {editing === f.name ? (
                  <span className="env-val-acts">
                    <input
                      className="input input--mono"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit();
                        if (e.key === 'Escape' && !saving) setEditing(null);
                      }}
                      style={{ width: 240, padding: '6px 12px' }}
                      disabled={saving}
                      autoFocus
                      aria-label={`New value for ${f.name}`}
                    />
                    <button type="button" className="btn btn--sm btn--primary" onClick={saveEdit} disabled={saving || !draft.trim()}>
                      {saving ? <span className="spin" style={{ borderTopColor: '#fff' }} /> : <Icon name="check" size={12} />} Save
                    </button>
                    <button type="button" className="btn btn--sm btn--ghost" onClick={() => setEditing(null)} disabled={saving}>
                      Cancel
                    </button>
                  </span>
                ) : (
                  <span className="env-val-acts">
                    <span className="val">{f.value}</span>
                    <button type="button" className="btn btn--sm" onClick={() => startEdit(f)} title={`Change ${f.name}`}>
                      Change
                    </button>
                    <button
                      type="button"
                      className="btn btn--sm btn--ghost"
                      style={{ color: 'var(--danger)' }}
                      onClick={() => {
                        setRemoveError(null);
                        setPendingRemove(f);
                      }}
                      title={`Remove ${f.name} so the server default applies again`}
                    >
                      <Icon name="x" size={12} /> Remove
                    </button>
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      <Confirm
        open={!!pendingRemove}
        title={pendingRemove ? `Remove ${pendingRemove.name}?` : ''}
        body={`Plot ${plot.plotId} goes back to the server default for this flag. You can set it again at any time.`}
        confirmLabel="Remove flag"
        danger
        busy={removing}
        error={removeError}
        onCancel={() => !removing && setPendingRemove(null)}
        onConfirm={remove}
      />
    </>
  );
};

export default PlotFlagsTab;
